/**
 * authScene.ts — the sign-in scene: the town square, drawn by the town's own
 * art. Not an illustration of the dungeon door; the same square the player
 * lands in, rendered through `townScene.ts` (door, braziers, seal, plane,
 * pools) and `WorldArt` (floor tiles, terrain props) with the Viewport's own
 * floor-wear function. One implementation, so the page you sign in through
 * and the town you sign in to cannot drift.
 *
 * It is a still, not a second hub:
 *   - no socket, no roster, no presence. The town's people come from the
 *     server (`onHubState`) behind the token check; an anonymous pre-auth
 *     presence feed would leak who is online to every visitor and open a
 *     second path into `net/hub.ts`. An empty square is also honest — it is
 *     the town before you arrive.
 *   - no camera and no clock of consequence: the static square is drawn once
 *     into an offscreen layer and blitted; only the creator's hero redraws,
 *     on the host's rAF clock.
 *   - if the atlas fails, draw nothing (RENDERING.md). The form works
 *     regardless — a missing picture must never block sign-in.
 */
import { TERRAIN_POND, TERRAIN_TABLE } from "@pd/shared";
import type { Outfit } from "./atlas.js";
import type { HeroAssets } from "./hero.js";
import { drawHero, idleFrame } from "./hero.js";
import type { HeroLut } from "./heroLut.js";
import { HALF_H, HALF_W, TILE_H, TILE_W } from "./iso.js";
import {
  PROP_POND,
  PROP_TABLE,
  floorFrame,
  type WorldArt,
} from "./worldArt.js";
import {
  BRAZIER_TILES,
  CROWD_SLOTS,
  DOOR_ANCHOR,
  DOOR_ROW,
  SEAL_TILE,
  TOWN_FLOOR_ARCHITECTURE,
  TOWN_FLOOR_PALETTE,
  TOWN_FLOOR_WEAR,
  TOWN_PLANE_H,
  TOWN_PLANE_W,
  TOWN_SURFACE,
  drawBrazier,
  drawPlazaSeal,
  drawTownDoor,
} from "./townScene.js";

/** The camera, in the two numbers that compose the shot: the iso unit scale,
 *  and where the door's ground line sits as a fraction of the frame height.
 *  `cxOff` pushes the square's centre right of the frame's, because the form
 *  panel owns the left. Narrow frames (a phone) pull in and drop the offset. */
const U_WIDE = 1.2;
const U_NARROW = 0.72;
const HORIZON_WIDE = 0.56;
const HORIZON_NARROW = 0.62;
const CX_OFF_WIDE = 60;
const NARROW_BELOW = 900;

/** The preview hero stands in the crowd band — the same slots the hub hands a
 *  real arrival — so the creator shows the hero at the size and place it will
 *  actually be seen. Which slot is a composition question, not a gameplay
 *  one: on a wide frame the form panel owns the left, so the hero takes the
 *  band's shallow row right of centre (at the door's right foot); on a narrow
 *  frame the panel spans the width, so it takes the middle of that row. */
function heroTile(narrow: boolean): { x: number; y: number } {
  const want = narrow ? 1 : 3; // x−y: how far right of the axis the slot sits
  const band = CROWD_SLOTS.filter((s) => s.x - s.y === want);
  const row = band.length > 0 ? band : CROWD_SLOTS;
  // Shallowest depth = nearest the threshold = highest in the frame, i.e. the
  // slot furthest from the panel's bottom edge.
  return row.reduce((a, b) => (b.x + b.y < a.x + a.y ? b : a));
}

export const SCENE_HERO_TILE_WIDE = heroTile(false);
export const SCENE_HERO_TILE_NARROW = heroTile(true);

/** What stands on a tile, in the painter pass. Data, not closures: the same
 *  vocabulary the Viewport's terrain ids and the town's prop tiles use. */
type PropKind = "pond" | "table" | "brazier" | "seal" | "door";
type SceneProp = { x: number; y: number; kind: PropKind };

/** The town's standing props, collected once from the shared surface. */
function townProps(): SceneProp[] {
  const props: SceneProp[] = [];
  for (let y = 0; y < TOWN_PLANE_H; y++) {
    for (let x = 0; x < TOWN_PLANE_W; x++) {
      const t = TOWN_SURFACE.terrain[y * TOWN_PLANE_W + x];
      if (t === TERRAIN_POND) props.push({ x, y, kind: "pond" });
      else if (t === TERRAIN_TABLE) props.push({ x, y, kind: "table" });
    }
  }
  for (const b of BRAZIER_TILES) props.push({ x: b.x, y: b.y, kind: "brazier" });
  props.push({ x: SEAL_TILE.x, y: SEAL_TILE.y, kind: "seal" });
  props.push({ x: DOOR_ANCHOR.x, y: DOOR_ANCHOR.y, kind: "door" });
  return props.sort(byDepth);
}

/** The painter order the Viewport uses: depth x+y, then x, then y. */
function byDepth(a: { x: number; y: number }, b: { x: number; y: number }): number {
  return a.x + a.y === b.x + b.y ? (a.x === b.x ? a.y - b.y : a.x - b.x) : a.x + a.y - (b.x + b.y);
}

/** The hero the creator is building, as the scene draws it. */
export interface SceneHero {
  assets: HeroAssets;
  outfit: Outfit;
  lut: HeroLut | null;
}

/** The composed shot: unit scale + the screen origin of tile (0,0). */
interface Shot {
  u: number;
  ox: number;
  oy: number;
  cw: number;
  ch: number;
  hero: { x: number; y: number };
}

export class AuthScene {
  /** The square without the hero, cached at device resolution and blitted. */
  private layer: HTMLCanvasElement | null = null;
  private layerKey = "";
  private readonly props = townProps();
  private pendingPaths: string[] = [];
  /** The hero of the last drawn frame — a self-heal redraw must not drop it. */
  private lastHero: SceneHero | null = null;
  /** Bounded, so a permanently-missing frame cannot spin the redraw loop. */
  private heals = 0;

  constructor(
    private cv: HTMLCanvasElement,
    private art: WorldArt,
  ) {}

  /** Frames that were needed but still decoding during the last pass. The
   *  host re-draws once they land — a null cached mid-decode is not a
   *  permanent answer (traps/decode-race.md). */
  get missed(): string[] {
    return this.pendingPaths;
  }

  /** The shot for the current frame size. */
  private shot(): Shot | null {
    const cw = this.cv.clientWidth;
    const ch = this.cv.clientHeight;
    if (cw < 2 || ch < 2) return null;
    const narrow = cw < NARROW_BELOW;
    const u = narrow ? U_NARROW : U_WIDE;
    // The door's ground line is the shot's horizon; the plane hangs off it.
    const doorGround = ch * (narrow ? HORIZON_NARROW : HORIZON_WIDE);
    const ox = cw / 2 + (narrow ? 0 : CX_OFF_WIDE);
    return {
      u,
      ox,
      oy: doorGround - (DOOR_ROW * HALF_H + HALF_H) * u,
      cw,
      ch,
      hero: heroTile(narrow),
    };
  }

  private static sx0(s: Shot, x: number, y: number): number {
    return (x - y) * HALF_W * s.u + s.ox;
  }
  private static sy0(s: Shot, x: number, y: number): number {
    return (x + y) * HALF_H * s.u + s.oy;
  }

  /** Draw one frame: the cached square, then the hero (idle-breathing). */
  draw(tMs: number, hero: SceneHero | null): void {
    this.lastHero = hero;
    const s = this.shot();
    if (!s) return;
    const dpr = window.devicePixelRatio || 1;
    const w = Math.floor(s.cw * dpr);
    const h = Math.floor(s.ch * dpr);
    if (this.cv.width !== w || this.cv.height !== h) {
      this.cv.width = w;
      this.cv.height = h;
      this.layerKey = ""; // the cached layer no longer matches the frame
    }
    const ctx = this.cv.getContext("2d");
    if (!ctx) return;

    const key = `${s.u}|${s.ox}|${s.oy}|${w}x${h}`;
    if (!this.layer || this.layerKey !== key) this.renderSquare(s, key);

    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.imageSmoothingEnabled = false;
    if (this.layer) ctx.drawImage(this.layer, 0, 0);
    else {
      ctx.fillStyle = "#101016";
      ctx.fillRect(0, 0, w, h);
    }
    if (!hero) return;

    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    const hx = AuthScene.sx0(s, s.hero.x, s.hero.y);
    const hy = AuthScene.sy0(s, s.hero.x, s.hero.y) + HALF_H * s.u;
    this.art.drawContactShadow(ctx, hx, hy, s.u, 16, 8);
    drawHero(ctx, hero.assets, "canon", hero.outfit, "idle", "s", idleFrame(tMs), hx, hy, s.u, hero.lut);
  }

  /** The square on its own: floors first (flat, never overlap, so the door
   *  is not overpainted), then everything that stands up in painter order. */
  private renderSquare(s: Shot, key: string): void {
    const layer = this.layer ?? document.createElement("canvas");
    layer.width = this.cv.width;
    layer.height = this.cv.height;
    const ctx = layer.getContext("2d");
    if (!ctx) return;
    const dpr = window.devicePixelRatio || 1;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.imageSmoothingEnabled = false;
    ctx.fillStyle = "#101016";
    ctx.fillRect(0, 0, s.cw, s.ch);

    this.art.beginPass();

    for (let y = 0; y < TOWN_PLANE_H; y++) {
      for (let x = 0; x < TOWN_PLANE_W; x++) {
        const sx0 = AuthScene.sx0(s, x, y);
        const sy0 = AuthScene.sy0(s, x, y);
        if (sx0 < -TILE_W * s.u || sx0 > s.cw + TILE_W * s.u) continue;
        if (sy0 < -TILE_H * 4 * s.u || sy0 > s.ch + TILE_H * s.u) continue;
        this.art.drawFloorTile(
          ctx,
          sx0,
          sy0,
          s.u,
          this.art.floorTile(TOWN_FLOOR_PALETTE, floorFrame(TOWN_FLOOR_ARCHITECTURE, TOWN_FLOOR_WEAR, x, y)),
        );
      }
    }

    for (const p of this.props) {
      const sx0 = AuthScene.sx0(s, p.x, p.y);
      const sy0 = AuthScene.sy0(s, p.x, p.y);
      // A prop may stand far above its own tile (the door is ~3 tiles tall),
      // so the cull box is generous upward, tight downward.
      if (sx0 < -TILE_W * 3 * s.u || sx0 > s.cw + TILE_W * 3 * s.u) continue;
      if (sy0 < -TILE_H * 12 * s.u || sy0 > s.ch + TILE_H * 4 * s.u) continue;
      this.drawProp(ctx, s, p, sx0, sy0);
    }

    this.pendingPaths = this.art.drainMissed();
    // Frames that were still decoding during this pass are not a permanent
    // answer: load them and redraw the square. The page has no clock of its
    // own when there is no hero, so nothing else would ever ask again
    // (traps/decode-race.md). Bounded — a frame that never arrives must not
    // spin.
    if (this.pendingPaths.length > 0 && this.heals < 4) {
      this.heals++;
      void this.art.ensureLoaded(this.pendingPaths).then(() => {
        this.layerKey = "";
        this.draw(performance.now(), this.lastHero);
      });
    } else if (this.pendingPaths.length === 0) {
      this.heals = 0;
    }

    this.layer = layer;
    this.layerKey = key;
  }

  private drawProp(
    ctx: CanvasRenderingContext2D,
    s: Shot,
    p: SceneProp,
    sx0: number,
    sy0: number,
  ): void {
    const ground = sy0 + HALF_H * s.u;
    switch (p.kind) {
      case "pond":
        this.art.drawAnchored(ctx, sx0, ground, s.u, PROP_POND);
        break;
      case "table":
        this.art.drawAnchored(ctx, sx0, ground, s.u, PROP_TABLE);
        break;
      case "brazier":
        drawBrazier(ctx, s.u, sx0, sy0);
        break;
      case "seal":
        drawPlazaSeal(ctx, s.u, sx0, sy0);
        break;
      case "door":
        drawTownDoor(ctx, s.u, sx0, sy0);
        break;
    }
  }

  /** Force the square to redraw (a resize, or frames that just decoded). */
  invalidate(): void {
    this.layerKey = "";
  }
}
